// WarehouseQuoteDetailCreate.tsx
import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ServiceResponse } from '../../../../../app/services/shared/interfaces';
import ButtonCancelForm from '../../../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../../../components/buttons/ButtonSaveForm';
import useWarehouseQuoteDetailService from '../../../../../app/services/hooks/useWarehouseQuoteDetailService';
import { defaultWarehouseQuoteDetailDataForm, WarehouseQuoteDetailDataForm } from '../../../../../app/models/WareHouseQuoteDetail';
import WarehouseQuoteDetailForm from './forms/WareHouseQuoteDetailForms/WareHouseQuoteDetailForm';
import useReactConfirmAlert from '../../../../../hooks/useReactConfirmAlert';
import { Service } from '../../../../../app/models/Service';
import { Product } from '../../../../../app/models/Product';
import { Unit } from '../../../../../app/models/Unit';
import { Currency } from '../../../../../app/models/Currency';
import { Depot } from '../../../../../app/models/Depot';
import { AppContext } from '../../../../../contexts/AppContext';
import { QuoteDetailFormTypeEnum } from '../../../../../app/shared/enums';

interface Props {
    quoteId: number;
    sectorId: number;
    onError?: () => void;
    onSaved: () => void;
    onCancel: () => void;
}

const WarehouseQuoteDetailCreate = ({ 
    quoteId,
    sectorId,
    onSaved,
    onCancel,
    onError,
}: Props) => {
    const { storeWarehouseQuoteDetail, createWarehouseQuoteDetail, fetchingShowStorageDataWareHouseQuoteDetail, showStorageDataToWareHouseQuoteDetail } = useWarehouseQuoteDetailService();
    const [quoteDetailForm, setQuoteDetailForm] = useState<WarehouseQuoteDetailDataForm>({
        depot_id: undefined,
        product_id: 0,
        quote_id: quoteId
    });
    const [depots, setDepots] = useState<Depot[]>([]);
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    
    const [services, setServices] = useState<Service[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [quantityUnits, setQuantityUnits] = useState<Unit[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [units, setUnits] = useState<Unit[]>([]);
    const [errorFields, setErrorFields] = useState<any>();
    const [disableStorage, setDisableStorage] = useState<boolean>(false);
    const [disableRate, setDisableRate] = useState<boolean>(false);

    const isWarehouseForm = services.find(s => s.id === quoteDetailForm.service_id)
        ?.quote_detail_form_type === QuoteDetailFormTypeEnum.WAREHOUSE;
    
    useEffect(() => {
        if (!sectorId) {
            useReactConfirmAlert().errorAlert({
                title: 'Error',
                message: 'Debe seleccionar un sector antes de agregar una posición.'
            });
            if (onError) onError();
            return;
        }

        createWarehouseQuoteDetail(sectorId, {
            onSuccess: (response: ServiceResponse) => {
                setProducts(response.data.products);
                setQuantityUnits(response.data.quantity_units);
                setCurrencies(response.data.currencies);
                setDepots(response.data.depots);
                setUnits(response.data.units);
                setServices(response.data.services);
            },
            onError: (response: ServiceResponse) => {
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
                if (onError) onError();
            }
        });
    }, []);

    const store = () => {
        storeWarehouseQuoteDetail(quoteId,quoteDetailForm, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });
                toast.success(response.message, {
                    autoClose: 2500
                });
                if (onSaved) onSaved();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();

                setErrorFields(errorFields.data);
            }
        });
    };

    // Función para mapear los datos del API al formulario
    const mapStorageDataToForm = (storageData: any) => {
        const freeDaysData = storageData.free_days?.[0];
        
        if (!freeDaysData) {
            return {}; // No hay datos para mapear
        }

        // Función helper para encontrar el ID de una unidad por nombre
        const findUnitIdByName = (unitName: string) => {
            const unit = units.find(u => u.name === unitName);
            return unit ? unit.id : 0;
        };

        // Función helper para encontrar el ID de una moneda por nombre
        const findCurrencyIdByName = (currencyName: string) => {
            const currency = currencies.find(c => c.name === currencyName);
            return currency ? currency.id : 0;
        };

        return {
            // Días libres
            free_days: freeDaysData?.free_days || 0,
            
            // Comentarios adicionales
        };
    };

    // Limpiar campos warehouse cuando el servicio no es WAREHOUSE
    useEffect(() => {
        if (quoteDetailForm.service_id && services.length > 0 && !isWarehouseForm) {
            setQuoteDetailForm(prev => ({
                ...prev,
                depot_id: undefined,
                rate_period: undefined,
                free_days: 0,
                comment: '',
            }));
        }
    }, [quoteDetailForm.service_id, services.length]);

    useEffect(() => {
        // Solo ejecutar si ambos valores están presentes y las listas de monedas/unidades están cargadas
        if (quoteDetailForm.depot_id && quoteDetailForm.product_id && currencies.length > 0 && units.length > 0) {
            showStorageDataToWareHouseQuoteDetail(quoteId, quoteDetailForm.depot_id, quoteDetailForm.product_id, {
                onSuccess: (response: ServiceResponse) => {
                    setErrorFields(undefined);
                    
                    // Mapear los datos del almacenamiento al formulario
                    const mappedStorageData = mapStorageDataToForm(response.data);
                    setDisableStorage(response.data.has_storage)
                    setQuoteDetailForm(prevForm => ({
                        ...prevForm,
                        ...mappedStorageData
                    }));

                },
                onError: (response: ServiceResponse) => {
                    // Si no se encuentran datos, limpiar los campos relacionados
                    setQuoteDetailForm(prevForm => ({
                        ...prevForm,
                        free_days: 0,
                        comment: ''
                    }));
                }
            });
        }
    }, [quoteDetailForm.product_id, quoteDetailForm.depot_id, currencies.length, units.length]);

    return (
        <>
            <WarehouseQuoteDetailForm
                warehouseQuoteDetailForm={quoteDetailForm} 
                setWarehouseQuoteDetailForm={setQuoteDetailForm} 
                services={services}
                products={products} 
                currencies={currencies} 
                units={units}
                depots={depots}
                quantityUnits={quantityUnits}
                loadingStorageInfo={fetchingShowStorageDataWareHouseQuoteDetail} 
                disableStorage={disableStorage}
                disableRate={disableRate}
                errorFields={errorFields}
                isWarehouseForm={isWarehouseForm}
            />
            <hr />
            <div className="row justify-content-end">
                <div className="col-auto"><ButtonCancelForm callbackCancel={onCancel} /></div>
                <div className="col-auto"><ButtonSaveForm callbackSave={store} /></div>
            </div>
        </>
    );
};

export default WarehouseQuoteDetailCreate;